import { z } from 'zod';
import { PAGINATION } from './constants';

/**
 * Reusable field validators. FRD §7 is the authority for every rule here;
 * the server enforces them, the client reuses them for immediate feedback.
 */

/** Indian mobile: exactly 10 digits, first digit 6-9. */
export const indianMobileSchema = z
  .string()
  .trim()
  .regex(/^[6-9]\d{9}$/, 'Enter a valid 10-digit Indian mobile number');

/** Exactly 6 digits. */
export const pincodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'PIN code must be 6 digits');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email('Enter a valid email address')
  .max(160, 'Email must be 160 characters or fewer');

/** FR-AUTH-005 — minimum 8 characters, at least one letter and one digit. */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(72, 'Password must be 72 characters or fewer')
  .regex(/[A-Za-z]/, 'Password must contain at least one letter')
  .regex(/\d/, 'Password must contain at least one digit');

/**
 * Money as a string with at most 2 decimal places. Kept as a string end to end so no
 * amount ever passes through a JS float (FRD §2.3).
 */
export const moneySchema = z
  .string()
  .regex(/^\d{1,10}(\.\d{1,2})?$/, 'Enter a valid amount with up to 2 decimal places');

export const positiveMoneySchema = moneySchema.refine(
  (v) => Number(v) > 0,
  'Amount must be greater than zero',
);

/** FR-CALC-001 — quantity greater than zero, at most 2 decimal places. */
export const quantitySchema = z
  .string()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, 'Enter a valid quantity')
  .refine((v) => Number(v) > 0, 'Quantity must be greater than zero');

/** ISO-8601 date (YYYY-MM-DD) on the wire, per FRD §2.3. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

export const idSchema = z.coerce.number().int().positive();

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(PAGINATION.defaultPage),
  limit: z.coerce.number().int().min(1).max(PAGINATION.maxLimit).default(PAGINATION.defaultLimit),
  sort: z.string().trim().max(40).optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().trim().max(120).optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

/** Percentage between 0 and 100 inclusive (FRD §7, discount percent). */
export const percentSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Enter a valid percentage')
  .refine((v) => Number(v) >= 0 && Number(v) <= 100, 'Percentage must be between 0 and 100');
